import * as React from 'react';
import {
  Box,
  Breadcrumbs,
  Chip,
  Divider,
  Link,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowLeft as ArrowLeftIcon } from '@phosphor-icons/react/dist/ssr/ArrowLeft';
import { CaretDown as CaretDownIcon } from '@phosphor-icons/react';
import { CheckCircle as CheckCircleIcon } from '@phosphor-icons/react/dist/ssr/CheckCircle';
import { CalendarBlank as CalendarBlankIcon } from '@phosphor-icons/react/dist/ssr/CalendarBlank';
import { User as UserIcon } from '@phosphor-icons/react/dist/ssr/User';
import { Hash as HashIcon } from '@phosphor-icons/react/dist/ssr/Hash';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

import { type Request } from '@/types/v2/request';
import { useUser } from '@/hooks/v2/use-user';
import { useCommunityRole } from '@/hooks/v2/use-community-role';
import { getAllowedNextStatusIds } from '@/constants/request-status-flows';
import { toTitleCase } from '@/utils/to-title-case';
import { paths } from '@/paths';
import { dayjs } from '@/lib/v2/dayjs';
import axios from '@/lib/v2/axios';

interface RequestHeaderProps {
  request?: Request | null;
  isLoading: boolean;
  error: any;
}

interface RequestStatus {
  id: number;
  name: string;
  color: string;
  description: string;
}

const TYPE_COLORS: Record<string, string> = {
  maintenance: '#F59E0B',
  arc: '#8B5CF6',
  incident: '#EF4444',
  security: '#EF4444',
  general: '#3B82F6',
  suggestions: '#10B981',
};

export function RequestHeader({ request, isLoading, error }: RequestHeaderProps): React.JSX.Element {
  const { user } = useUser();
  const { hasBoardLevelAccess } = useCommunityRole();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [statusMenuAnchor, setStatusMenuAnchor] = React.useState<null | HTMLElement>(null);

  const canEditStatus = !!user && hasBoardLevelAccess;

  const { data: statuses } = useQuery<RequestStatus[]>({
    queryKey: ['request-statuses'],
    queryFn: async () => {
      const response = await axios.get('/api/v1/requests/statuses');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const updateStatusMutation = useMutation({
    mutationFn: async (statusId: number) => {
      const response = await axios.put(`/api/v2/communities/${request?.community_id}/requests/${request?.id}`, {
        status_id: statusId,
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Status updated');
      queryClient.invalidateQueries({ queryKey: ['request'] });
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      setStatusMenuAnchor(null);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to update status');
    },
  });

  const currentStatus = request?.request_status;
  const typeName = request?.request_type?.name ?? '';
  const typeColor = TYPE_COLORS[typeName] ?? '#6B7280';
  const allowedStatusIds = getAllowedNextStatusIds(typeName, request?.status_id);

  if (isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton height={20} width={200} variant="text" />
        <Skeleton height={36} width={400} variant="text" />
        <Skeleton height={20} width={300} variant="text" />
      </Stack>
    );
  }

  return (
    <Stack spacing={2.5}>
      {/* Breadcrumb */}
      <Breadcrumbs
        separator={<Typography sx={{ color: 'text.disabled', fontSize: '0.8125rem', mx: -0.5 }}>/</Typography>}
        sx={{ fontSize: '0.8125rem' }}
      >
        <Link
          component="button"
          onClick={() => { navigate(paths.requests.index); }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'text.secondary',
            textDecoration: 'none',
            fontSize: 'inherit',
            '&:hover': { color: 'text.primary' },
          }}
          underline="none"
        >
          <ArrowLeftIcon size={13} />
          Requests
        </Link>
        <Typography color="text.primary" sx={{ fontSize: 'inherit', fontWeight: 500 }}>
          {request?.display_id ? `#${request.display_id}` : request?.id ? `#${String(request.id).slice(0, 8)}` : 'Request'}
        </Typography>
      </Breadcrumbs>

      {/* Title row */}
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <Stack spacing={1}>
          <Typography sx={{ fontWeight: 700, lineHeight: 1.25 }} variant="h4">
            {request?.summary ?? 'Untitled Request'}
          </Typography>

          {/* Meta row */}
          <Stack direction="row" spacing={2.5} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
            {/* Type badge */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.25,
                py: 0.25,
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.02em',
                backgroundColor: `${typeColor}18`,
                color: typeColor,
                border: `1px solid ${typeColor}40`,
              }}
            >
              {toTitleCase(typeName || 'Request')}
            </Box>

            {/* Divider dot */}
            <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />

            {/* Request ID */}
            {request?.display_id || request?.number ? (
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: 'text.secondary' }}>
                <HashIcon size={13} />
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {request.display_id ?? request.number}
                </Typography>
              </Stack>
            ) : null}

            {/* Submitted by */}
            {request?.created_by && (
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: 'text.secondary' }}>
                <UserIcon size={13} />
                <Typography variant="caption">
                  {request.created_by.first_name} {request.created_by.last_name}
                </Typography>
              </Stack>
            )}

            {/* Date */}
            {request?.created_at && (
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: 'text.secondary' }}>
                <CalendarBlankIcon size={13} />
                <Typography variant="caption">
                  {dayjs(request.created_at).format('MMM D, YYYY')}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>

        {/* Status control */}
        <Box sx={{ flexShrink: 0 }}>
          {canEditStatus ? (
            <>
              <Tooltip title="Change status">
                <Chip
                  deleteIcon={<CaretDownIcon size={14} />}
                  disabled={updateStatusMutation.isPending}
                  label={toTitleCase(currentStatus?.name ?? 'Unknown')}
                  onDelete={(e) => { setStatusMenuAnchor(e.currentTarget as HTMLElement); }}
                  onClick={(e) => { setStatusMenuAnchor(e.currentTarget); }}
                  sx={{
                    backgroundColor: currentStatus?.color ?? '#6B7280',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    height: 32,
                    cursor: 'pointer',
                    '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } },
                  }}
                />
              </Tooltip>
              <Menu
                anchorEl={statusMenuAnchor}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                onClose={() => { setStatusMenuAnchor(null); }}
                open={Boolean(statusMenuAnchor)}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { minWidth: 220, mt: 0.5 } } }}
              >
                {statuses
                  ?.filter((s) => allowedStatusIds.includes(s.id))
                  .map((s) => (
                    <MenuItem
                      disabled={updateStatusMutation.isPending}
                      key={s.id}
                      onClick={() => { updateStatusMutation.mutate(s.id); }}
                      selected={s.id === request?.status_id}
                      sx={{ gap: 1.5 }}
                    >
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />
                      <Typography variant="body2">{toTitleCase(s.name)}</Typography>
                      {s.id === request?.status_id && (
                        <CheckCircleIcon size={14} style={{ marginLeft: 'auto', opacity: 0.6 }} />
                      )}
                    </MenuItem>
                  ))}
              </Menu>
            </>
          ) : currentStatus ? (
            <Chip
              label={toTitleCase(currentStatus.name)}
              sx={{
                backgroundColor: currentStatus.color,
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.8125rem',
                height: 32,
              }}
            />
          ) : null}
        </Box>
      </Stack>
    </Stack>
  );
}
